/**
 * Wave 8 · AI agent cost registry and branded surface labels.
 * Extracted as a pure constants module to avoid pulling Firebase/DOM dependencies
 * into UI components and light certification consumers.
 */

export const AI_AGENT_COSTS = {
  PULSE_SUMMARY: 5,
  GOVERNANCE_REVIEW: 10,
  BIG_IDEA: 25,
  EVIDENCE_READING_SURCHARGE: 20,
  RISK_SCANNER: 3,
  BUDGET_ADVISOR: 4,
  PULSE_REPORT_SNAPSHOT: 15,
  PULSE_REPORT_RISK: 3,
  PULSE_REPORT_BUDGET: 4,
  PULSE_REPORT_DIGEST: 4,
  PULSE_REPORT_GOVERNANCE: 10,
  REPORT_REPHRASE: 6,
  DIGEST_REPHRASE: 4,
  ANALYSIS_SUMMARY: 4,
  AI_CHART_DRAFT: 2,
  AI_CHART_EXPLAIN: 5,
  AI_CHART_SUGGEST: 10,
} as const

/**
 * Wave 8 · Branded surface labels for the named AI entry points.
 * Every AI surface visible to the user maps to a stable label and a cost
 * key from AI_AGENT_COSTS. New entry points must extend this map so the
 * AI Action Certification Matrix (Wave 8 §5a item 14) can enforce coverage.
 */
export const AI_SURFACE_LABELS = {
  PROJECT_ARCHITECT: {
    label: 'AI Project Architect',
    tagline: 'Paragraph → goal / task / checklist tree (pending-tree save).',
    costKey: 'BIG_IDEA',
  },
  RISK_SCANNER: {
    label: 'AI Risk Scanner',
    tagline: 'Deterministic risk read over the workspace graph and activity.',
    costKey: 'RISK_SCANNER',
  },
  BUDGET_ADVISOR: {
    label: 'AI Budget Advisor',
    tagline: 'Reads Budget & Stock services and emits a targeted advisory.',
    costKey: 'BUDGET_ADVISOR',
  },
  PULSE_SUMMARY: {
    label: 'Workspace Pulse',
    tagline: 'Mission-control narrative summary over the workspace pulse feed.',
    costKey: 'PULSE_SUMMARY',
  },
  GOVERNANCE_REVIEW: {
    label: 'AI Governance Review',
    tagline: 'Compliance & governance narrative over the audit ledger.',
    costKey: 'GOVERNANCE_REVIEW',
  },
  REPORT_REPHRASE: {
    label: 'AI Report Rephraser',
    tagline: 'Turns deterministic report content into a natural-language narrative.',
    costKey: 'REPORT_REPHRASE',
  },
  DIGEST_REPHRASE: {
    label: 'AI Digest Rephraser',
    tagline: 'Turns notification digest events into a natural-language summary.',
    costKey: 'DIGEST_REPHRASE',
  },
} as const

export type AiSurfaceKey = keyof typeof AI_SURFACE_LABELS
